import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  SearchOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import { SignatureValidationContainer } from './styles';

const { Title, Text, Paragraph } = Typography;

interface ValidationFormValues {
  organization_code: string;
  document_type: string;
  code?: string;
  validation_code?: string;
  id?: string;
}

const documentTypeOptions = [
  { label: 'Atestado', value: 'HEALTH_CERTIFICATE' },
  { label: 'Documento', value: 'DOCUMENT' },
  { label: 'Receituário', value: 'PRESCRIPTION' },
  { label: 'Prontuário', value: 'FORM' },
  { label: 'Evolução de Prontuário', value: 'FORM_EVOLUTION' },
  { label: 'Tratamento', value: 'TREATMENT' },
  { label: 'Evolução de Tratamento', value: 'TREATMENT_EVOLUTION' },
  { label: 'Tratamento Odontológico', value: 'DENTAL_TREATMENT' },
  {
    label: 'Evolução de Tratamento Odontológico',
    value: 'DENTAL_TREATMENT_EVOLUTION',
  },
];

const documentTypeLabels = documentTypeOptions.reduce<Record<string, string>>(
  (accumulator, option) => {
    accumulator[option.value] = option.label;
    return accumulator;
  },
  {},
);

const statusMap: Record<
  string,
  { color: string; title: string; icon: React.ReactNode }
> = {
  valid: {
    color: '#389e0d',
    title: 'Documento válido',
    icon: <CheckCircleOutlined style={{ color: '#389e0d', fontSize: 28 }} />,
  },
  unsigned: {
    color: '#d48806',
    title: 'Documento sem assinatura digital',
    icon: (
      <SafetyCertificateOutlined style={{ color: '#d48806', fontSize: 28 }} />
    ),
  },
  invalid: {
    color: '#cf1322',
    title: 'Documento inválido',
    icon: <CloseCircleOutlined style={{ color: '#cf1322', fontSize: 28 }} />,
  },
  inconclusive: {
    color: '#1d39c4',
    title: 'Não foi possível confirmar',
    icon: (
      <SafetyCertificateOutlined style={{ color: '#1d39c4', fontSize: 28 }} />
    ),
  },
};

const SignatureValidation: React.FC = () => {
  const [form] = Form.useForm<ValidationFormValues>();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const savedOrganizationCode = sessionStorage.getItem('@Sigeclin:clinic');
    const initialOrganizationCode = savedOrganizationCode || '';

    if (initialOrganizationCode) {
      form.setFieldsValue({ organization_code: initialOrganizationCode });
      sessionStorage.setItem('@Sigeclin:clinic', initialOrganizationCode);
      api.defaults.headers['x-tenant-id'] = initialOrganizationCode;
    }
  }, [form]);

  const selectedStatus = useMemo(() => {
    if (!result?.status) {
      return null;
    }

    return statusMap[result.status] || statusMap.inconclusive;
  }, [result]);

  const onSubmit = async (values: any) => {
    setLoading(true);
    setError(null);
    setResult(null);

    const organizationCode = values.organization_code?.trim();

    sessionStorage.setItem('@Sigeclin:clinic', organizationCode);
    api.defaults.headers['x-tenant-id'] = organizationCode;

    try {
      const response = await api.post('/signature-validation/public/validate', {
        document_type: values.document_type,
        code: values.code || undefined,
        id: values.id || undefined,
        validation_code: values.validation_code || undefined,
      });
      setResult(response.data);
    } catch (err: any) {
      const message = err?.response?.data?.message;
      setError(
        Array.isArray(message)
          ? message[0]
          : message || 'Não foi possível validar o documento.',
      );
    } finally {
      setLoading(false);
    }
  };

  const validateIdentifier = async () => {
    const code = form.getFieldValue('code')?.trim();
    const validationCode = form.getFieldValue('validation_code')?.trim();
    const id = form.getFieldValue('id')?.trim();

    if (code || validationCode || id) {
      return Promise.resolve();
    }

    return Promise.reject(
      new Error('Informe pelo menos um dado do documento.'),
    );
  };

  const handleIdentifierChange = () => {
    form.validateFields(['code', 'validation_code', 'id']).catch(() => null);
  };

  return (
    <SignatureValidationContainer>
      <div className="validation-shell">
        <section className="hero-card">
          <div className="hero-badge">
            <SafetyCertificateOutlined />
            Validação pública
          </div>

          <div className="hero-grid">
            <div>
              <Title level={2} className="hero-title">
                Confira se o documento é verdadeiro em poucos passos
              </Title>
              <Paragraph className="hero-description">
                Informe a clínica ou empresa, escolha o tipo de documento e
                preencha um dos dados que aparecem no arquivo.
              </Paragraph>

              <div className="hero-steps">
                <div className="step-item">
                  <span className="step-number">1</span>
                  <div>
                    <strong>Código da clínica ou empresa</strong>
                    <p>Isso mostra onde o documento foi emitido.</p>
                  </div>
                </div>
                <div className="step-item">
                  <span className="step-number">2</span>
                  <div>
                    <strong>Tipo do documento</strong>
                    <p>Ajuda a encontrar o documento certo com mais rapidez.</p>
                  </div>
                </div>
                <div className="step-item">
                  <span className="step-number">3</span>
                  <div>
                    <strong>Um dado do documento</strong>
                    <p>
                      Você pode usar o código, o código de validação ou o ID.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Card className="help-card" bordered={false}>
              <Space direction="vertical" size={14} style={{ width: '100%' }}>
                <div>
                  <Text className="help-label">Melhor opção</Text>
                  <Paragraph className="help-text">
                    Se possível, use o código de validação que aparece no
                    documento.
                  </Paragraph>
                </div>

                <div>
                  <Text className="help-label">Se não tiver esse código</Text>
                  <Paragraph className="help-text">
                    Você também pode validar usando o código numérico ou o ID do
                    documento.
                  </Paragraph>
                </div>

                <Alert
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                  message="A consulta não altera o documento"
                  description="Ela apenas verifica se o conteúdo continua o mesmo e se a assinatura digital pode ser confirmada."
                />
              </Space>
            </Card>
          </div>
        </section>

        <Card className="form-card" bordered={false}>
          <div className="section-header">
            <div>
              <Title level={4} className="section-title">
                Dados para validação
              </Title>
              <Text className="section-description">
                Preencha os campos abaixo para fazer a consulta.
              </Text>
            </div>
            <Tag color="blue" className="section-tag">
              Consulta segura
            </Tag>
          </div>

          <Form form={form} layout="vertical" onFinish={onSubmit}>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="organization_code"
                  label="Código da clínica ou empresa"
                  extra="Exemplo: clinica-centro"
                  rules={[
                    {
                      required: true,
                      message: 'Informe o código da clínica ou empresa.',
                    },
                  ]}
                >
                  <Input placeholder="Digite o código informado pela clínica ou empresa" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="document_type"
                  label="Tipo do documento"
                  rules={[{ required: true, message: 'Selecione o tipo.' }]}
                >
                  <Select
                    placeholder="Selecione o tipo do documento"
                    options={documentTypeOptions}
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item
                  name="validation_code"
                  label="Código de validação"
                  validateTrigger={['onChange', 'onBlur']}
                  rules={[{ validator: validateIdentifier }]}
                >
                  <Input
                    placeholder="Ex: VAL-2026-ABCD"
                    onChange={handleIdentifierChange}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item
                  name="code"
                  label="Código do documento"
                  validateTrigger={['onChange', 'onBlur']}
                  rules={[{ validator: validateIdentifier }]}
                >
                  <Input
                    placeholder="Ex: 1024"
                    onChange={handleIdentifierChange}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item
                  name="id"
                  label="ID do registro"
                  extra="Use apenas se esse dado aparecer no documento"
                  validateTrigger={['onChange', 'onBlur']}
                  rules={[{ validator: validateIdentifier }]}
                >
                  <Input
                    placeholder="UUID do documento"
                    onChange={handleIdentifierChange}
                  />
                </Form.Item>
              </Col>
            </Row>

            <div className="form-actions">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SearchOutlined />}
                size="large"
              >
                Verificar documento
              </Button>
            </div>
          </Form>

          {loading && (
            <div className="loading-state">
              <Spin size="large" />
              <Text>Verificando assinatura digital...</Text>
            </div>
          )}

          {error && (
            <Alert
              className="feedback-alert"
              type="error"
              showIcon
              message="Erro na validação"
              description={error}
            />
          )}
        </Card>

        {result && selectedStatus && (
          <Card
            className="result-card"
            bordered={false}
            style={
              { '--status-color': selectedStatus.color } as React.CSSProperties
            }
          >
            <div className="result-header">
              <div className="result-status-icon">{selectedStatus.icon}</div>
              <div>
                <Title level={4} className="result-title">
                  {selectedStatus.title}
                </Title>
                <Paragraph className="result-message">
                  {result.message}
                </Paragraph>
              </div>
            </div>

            <div className="result-grid">
              <div className="result-item">
                <span className="result-label">Tipo</span>
                <strong>
                  {documentTypeLabels[result.document_type] ||
                    result.document_type}
                </strong>
              </div>
              <div className="result-item">
                <span className="result-label">Assinante</span>
                <strong>
                  {result.signer?.professional_name || 'Não informado'}
                </strong>
              </div>
              <div className="result-item">
                <span className="result-label">Código</span>
                <strong>{result.reference?.code || 'Não informado'}</strong>
              </div>
              <div className="result-item">
                <span className="result-label">Código de validação</span>
                <strong>
                  {result.reference?.validation_code || 'Não informado'}
                </strong>
              </div>
            </div>

            <Divider className="result-divider" />

            <div className="checks-grid">
              <div className="check-card">
                <span className="check-label">Possui assinatura</span>
                <strong>{result.checks?.has_signature ? 'Sim' : 'Não'}</strong>
              </div>

              <div className="check-card">
                <span className="check-label">Integridade do hash</span>
                <strong>
                  {result.checks?.hash_matches === null
                    ? 'N/A'
                    : result.checks?.hash_matches
                      ? 'Válida'
                      : 'Inválida'}
                </strong>
                {result.checks?.hash_matches === null ? null : result.checks
                    ?.hash_matches ? (
                  <CheckCircleOutlined className="check-icon success" />
                ) : (
                  <CloseCircleOutlined className="check-icon danger" />
                )}
              </div>

              <div className="check-card">
                <span className="check-label">Assinatura digital</span>
                <strong>
                  {result.checks?.cryptographic_signature_valid === null
                    ? 'N/A'
                    : result.checks?.cryptographic_signature_valid
                      ? 'Válida'
                      : 'Inválida'}
                </strong>
                {result.checks?.cryptographic_signature_valid ===
                null ? null : result.checks?.cryptographic_signature_valid ? (
                  <CheckCircleOutlined className="check-icon success" />
                ) : (
                  <CloseCircleOutlined className="check-icon danger" />
                )}
              </div>
            </div>
          </Card>
        )}
      </div>
    </SignatureValidationContainer>
  );
};

export default SignatureValidation;
